import type { ReactElement } from "react"
import React, { useState } from "react"
import { useFormContext } from "react-hook-form"

import AutoComplete from "@/components/common/Autocomplete/AutoComplete"
import { DEBOUNCE_DELAY_MS } from "@/constants/common"
import { DEFAULT_AUTOCOMPLETE_VALUE } from "@/constants/forms"
import type { Category } from "@/graphql/codegen/graphql"
import useProductCategory from "@/screens/ManageProducts/components/ProductCategories/hooks/useProductCategory"
import type { ProductCategoryFormValue } from "@/screens/ManageProducts/components/ProductClasses/forms/classSchema"
import type { AutocompleteOption } from "@/types/form-fields"
import { getAutoCompleteOptionLabel } from "@/utils/forms"
import useDebounce from "@/utils/useDebounce"

interface CategorySelectProps {
  onChange?: (category: string) => void
}

const CategorySelect = ({ onChange }: CategorySelectProps): ReactElement => {
  const { control } = useFormContext()
  const [query, setQuery] = useState("")
  const debouncedQuery = useDebounce(query, DEBOUNCE_DELAY_MS)

  const { productCategoryList } = useProductCategory({ query: debouncedQuery })
  const categoryOptions: ProductCategoryFormValue[] = productCategoryList.map((category: Category) => ({
    label: category.name,
    id: category.categoryId,
  }))

  const handleInputChange = (value: string) => {
    setQuery(value) // Update the query state
    if (onChange) {
      onChange(value)
    }
  }
  return (
    <AutoComplete<AutocompleteOption>
      onInputChange={handleInputChange}
      label='Category'
      placeholder='Select Category'
      id='category-select-field'
      options={categoryOptions}
      getOptionLabel={getAutoCompleteOptionLabel}
      name='category'
      control={control}
      defaultEmptyState={DEFAULT_AUTOCOMPLETE_VALUE}
      freeSolo={false}
      required
    />
  )
}

export default CategorySelect
